import { getSelectedOrganizationId } from '../../selectors/organizations/organizations';
import { GetState } from '../redux.types';

import { permissionService } from './permissions.service';
import {
  Permission,
  PermissionCreateDTO,
  RevisionCreateDTO,
} from './permissions.types';
import { getSelectedProjectId } from '../projects/projects.selectors';

export const PERMISSIONS_ACTION_NAMES = {
  LOAD_PERMISSIONS_START: 'PERMISSIONS_ACTION_NAMES--LOAD_PERMISSIONS_START',
  LOAD_PERMISSIONS_SUCCESS:
    'PERMISSIONS_ACTION_NAMES--LOAD_PERMISSIONS_SUCCESS',
  LOAD_PERMISSIONS_ERROR: 'PERMISSIONS_ACTION_NAMES--LOAD_PERMISSIONS_ERROR',

  CREATE_PERMISSION_START: 'PERMISSIONS_ACTION_NAMES--CREATE_PERMISSION_START',
  CREATE_PERMISSION_SUCCESS:
    'PERMISSIONS_ACTION_NAMES--CREATE_PERMISSION_SUCCESS',
  CREATE_PERMISSION_ERROR: 'PERMISSIONS_ACTION_NAMES--CREATE_PERMISSION_ERROR',

  DELETE_PERMISSION_START: 'PERMISSIONS_ACTION_NAMES--DELETE_PERMISSION_START',
  DELETE_PERMISSION_SUCCESS:
    'PERMISSIONS_ACTION_NAMES--DELETE_PERMISSION_SUCCESS',
  DELETE_PERMISSION_ERROR: 'PERMISSIONS_ACTION_NAMES--DELETE_PERMISSION_ERROR',

  UPDATE_PERMISSION_START: 'PERMISSIONS_ACTION_NAMES--UPDATE_PERMISSION_START',
  UPDATE_PERMISSION_SUCCESS:
    'PERMISSIONS_ACTION_NAMES--UPDATE_PERMISSION_SUCCESS',
  UPDATE_PERMISSION_ERROR: 'PERMISSIONS_ACTION_NAMES--UPDATE_PERMISSION_ERROR',

  ACTIVATE_PERMISSION_START:
    'PERMISSIONS_ACTION_NAMES--ACTIVATE_PERMISSION_START',
  ACTIVATE_PERMISSION_SUCCESS:
    'PERMISSIONS_ACTION_NAMES--ACTIVATE_PERMISSION_SUCCESS',
  ACTIVATE_PERMISSION_ERROR:
    'PERMISSIONS_ACTION_NAMES--ACTIVATE_PERMISSION_ERROR',

  DEACTIVATE_PERMISSION_START:
    'PERMISSIONS_ACTION_NAMES--DEACTIVATE_PERMISSION_START',
  DEACTIVATE_PERMISSION_SUCCESS:
    'PERMISSIONS_ACTION_NAMES--DEACTIVATE_PERMISSION_SUCCESS',
  DEACTIVATE_PERMISSION_ERROR:
    'PERMISSIONS_ACTION_NAMES--DEACTIVATE_PERMISSION_ERROR',

  LOAD_REVISIONS_START: 'PERMISSIONS_ACTION_NAMES--LOAD_REVISIONS_START',
  LOAD_REVISIONS_SUCCESS: 'PERMISSIONS_ACTION_NAMES--LOAD_REVISIONS_SUCCESS',
  LOAD_REVISIONS_ERROR: 'PERMISSIONS_ACTION_NAMES--LOAD_REVISIONS_ERROR',

  ACTIVATE_REVISION_START: 'PERMISSIONS_ACTION_NAMES--ACTIVATE_REVISION_START',
  ACTIVATE_REVISION_SUCCESS:
    'PERMISSIONS_ACTION_NAMES--ACTIVATE_REVISION_SUCCESS',
  ACTIVATE_REVISION_ERROR: 'PERMISSIONS_ACTION_NAMES--ACTIVATE_REVISION_ERROR',

  CREATE_REVISION_START: 'PERMISSIONS_ACTION_NAMES--CREATE_REVISION_START',
  CREATE_REVISION_SUCCESS: 'PERMISSIONS_ACTION_NAMES--CREATE_REVISION_SUCCESS',
  CREATE_REVISION_ERROR: 'PERMISSIONS_ACTION_NAMES--CREATE_REVISION_ERROR',

  LOAD_APPLICATION_GROUPS_START:
    'PERMISSIONS_ACTION_NAMES--LOAD_APPLICATION_GROUPS_START',
  LOAD_APPLICATION_GROUPS_SUCCESS:
    'PERMISSIONS_ACTION_NAMES--LOAD_APPLICATION_GROUPS_SUCCESS',
  LOAD_APPLICATION_GROUPS_ERROR:
    'PERMISSIONS_ACTION_NAMES--LOAD_APPLICATION_GROUPS_ERROR',
};

export const loadPermissions = () => (dispatch, getState: GetState) => {
  const state = getState();
  const organizationId = getSelectedOrganizationId(state);

  return dispatch({
    types: [
      PERMISSIONS_ACTION_NAMES.LOAD_PERMISSIONS_START,
      PERMISSIONS_ACTION_NAMES.LOAD_PERMISSIONS_SUCCESS,
      PERMISSIONS_ACTION_NAMES.LOAD_PERMISSIONS_ERROR,
    ],
    callApi: () => permissionService.getPermissions(organizationId),
  });
};

export const loadProjectPermissions = () => (dispatch, getState: GetState) => {
  const state = getState();
  const organizationId = getSelectedOrganizationId(state);
  const projectId = getSelectedProjectId(state);

  return dispatch({
    types: [
      PERMISSIONS_ACTION_NAMES.LOAD_PERMISSIONS_START,
      PERMISSIONS_ACTION_NAMES.LOAD_PERMISSIONS_SUCCESS,
      PERMISSIONS_ACTION_NAMES.LOAD_PERMISSIONS_ERROR,
    ],
    callApi: () =>
      permissionService.getProjectPermissions(organizationId, projectId),
  });
};

export const createPermission =
  (permission: PermissionCreateDTO) => (dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);
    const projectId = getSelectedProjectId(state);
    return dispatch({
      types: [
        PERMISSIONS_ACTION_NAMES.CREATE_PERMISSION_START,
        PERMISSIONS_ACTION_NAMES.CREATE_PERMISSION_SUCCESS,
        PERMISSIONS_ACTION_NAMES.CREATE_PERMISSION_ERROR,
      ],
      callApi: () =>
        permissionService.createPermission({
          permission,
          organizationId,
          projectId,
        }),
    });
  };

export const deletePermission =
  (permissionId: string) => (dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);

    return dispatch({
      types: [
        PERMISSIONS_ACTION_NAMES.DELETE_PERMISSION_START,
        PERMISSIONS_ACTION_NAMES.DELETE_PERMISSION_SUCCESS,
        PERMISSIONS_ACTION_NAMES.DELETE_PERMISSION_ERROR,
      ],
      additionalData: {
        permissionId,
      },
      callApi: () =>
        permissionService.deletePermission({ permissionId, organizationId }),
    });
  };

export const updatePermissionName =
  (params: { name: string; permissionId: string }) =>
  (dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);

    return dispatch({
      types: [
        PERMISSIONS_ACTION_NAMES.UPDATE_PERMISSION_START,
        PERMISSIONS_ACTION_NAMES.UPDATE_PERMISSION_SUCCESS,
        PERMISSIONS_ACTION_NAMES.UPDATE_PERMISSION_ERROR,
      ],
      callApi: () =>
        permissionService.updatePermissionName({ ...params, organizationId }),
    });
  };

export const activatePermission =
  (permission: Permission) => (dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);

    return dispatch({
      types: [
        PERMISSIONS_ACTION_NAMES.ACTIVATE_PERMISSION_START,
        PERMISSIONS_ACTION_NAMES.ACTIVATE_PERMISSION_SUCCESS,
        PERMISSIONS_ACTION_NAMES.ACTIVATE_PERMISSION_ERROR,
      ],
      additionalData: {
        permissionId: permission.id,
      },
      callApi: () =>
        permissionService.activatePermission({ permission, organizationId }),
    });
  };

export const deactivatePermission =
  (permission: Permission) => (dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);

    return dispatch({
      types: [
        PERMISSIONS_ACTION_NAMES.DEACTIVATE_PERMISSION_START,
        PERMISSIONS_ACTION_NAMES.DEACTIVATE_PERMISSION_SUCCESS,
        PERMISSIONS_ACTION_NAMES.DEACTIVATE_PERMISSION_ERROR,
      ],
      additionalData: {
        permissionId: permission.id,
      },
      callApi: () =>
        permissionService.deactivatePermission({ permission, organizationId }),
    });
  };

export const togglePermissionStatus =
  (permission: Permission) => (dispatch, getState: GetState) => {
    if (permission.active) {
      return deactivatePermission(permission)(dispatch, getState);
    }

    return activatePermission(permission)(dispatch, getState);
  };

export const loadRevisions =
  (permissionId: string) => (dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);

    return dispatch({
      types: [
        PERMISSIONS_ACTION_NAMES.LOAD_REVISIONS_START,
        PERMISSIONS_ACTION_NAMES.LOAD_REVISIONS_SUCCESS,
        PERMISSIONS_ACTION_NAMES.LOAD_REVISIONS_ERROR,
      ],
      additionalData: {
        permissionId,
      },
      callApi: () =>
        permissionService.getRevisions({ organizationId, permissionId }),
    });
  };

export const activateRevision =
  (revisionNumber: number, permissionId: string) =>
  (dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);

    return dispatch({
      types: [
        PERMISSIONS_ACTION_NAMES.ACTIVATE_REVISION_START,
        PERMISSIONS_ACTION_NAMES.ACTIVATE_REVISION_SUCCESS,
        PERMISSIONS_ACTION_NAMES.ACTIVATE_REVISION_ERROR,
      ],
      additionalData: {
        permissionId,
        revisionNumber,
      },
      callApi: () =>
        permissionService.activateRevision({
          revisionNumber,
          organizationId,
          permissionId,
        }),
    });
  };

export const createRevision =
  (revision: RevisionCreateDTO, permissionId: string) =>
  (dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);

    return dispatch({
      types: [
        PERMISSIONS_ACTION_NAMES.CREATE_REVISION_START,
        PERMISSIONS_ACTION_NAMES.CREATE_REVISION_SUCCESS,
        PERMISSIONS_ACTION_NAMES.CREATE_REVISION_ERROR,
      ],
      additionalData: {
        permissionId,
        activate: revision.activate,
      },
      callApi: () =>
        permissionService.createRevision({
          revision,
          organizationId,
          permissionId,
        }),
    });
  };

export const loadApplicationGroups = () => (dispatch, getState: GetState) => {
  const state = getState();
  const organizationId = getSelectedOrganizationId(state);

  return dispatch({
    types: [
      PERMISSIONS_ACTION_NAMES.LOAD_APPLICATION_GROUPS_START,
      PERMISSIONS_ACTION_NAMES.LOAD_APPLICATION_GROUPS_SUCCESS,
      PERMISSIONS_ACTION_NAMES.LOAD_APPLICATION_GROUPS_ERROR,
    ],
    callApi: () => permissionService.getApplicationGroups(organizationId),
  });
};
